"use client";

import Script from "next/script";
import { useEffect } from "react";

import { useConsent } from "@/features/consent/useConsent";

import { applyMetaConsent } from "./consent";
import { metaPixelSnippet } from "./pixel-snippet";

// Public, brand-specific pixel id. Absent → the pixel never loads (mirrors the
// TikTok pixel gate), so previews and local dev stay clean unless configured.
const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

/**
 * Loads the Meta (Facebook/Instagram) advertising pixel, gated behind the
 * `marketing` consent category. The pixel snippet is only injected once consent
 * is granted, so no `_fbp` cookie is set before the visitor opts in. If they
 * later revoke consent the <Script> unmounts, and the effect below tells the
 * still-loaded `fbq` to stop, so no further data is sent for the rest of the
 * session.
 */
export function MetaPixel() {
  const { marketing, ready } = useConsent();

  // Also covers re-granting within one session: `next/script` will not re-run
  // an inline script it has already executed under this `id`, so this effect —
  // not a remount — is what turns transmission back on.
  useEffect(() => {
    if (!ready) {
      return;
    }
    applyMetaConsent(marketing ?? "denied");
  }, [marketing, ready]);

  if (!ready || !PIXEL_ID || marketing !== "granted") {
    return null;
  }

  return (
    <Script id="meta-pixel" strategy="afterInteractive">
      {metaPixelSnippet(PIXEL_ID)}
    </Script>
  );
}
